import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Lock, Shield, CheckCircle } from 'lucide-react';
import PreLander from './PreLander';
import LoanForm from './LoanForm';
import VerificationScreen from './VerificationScreen';
import { leadService } from '../services/leadService';

const LoanFunnel = () => {
  const [step, setStep] = useState('prelander'); // prelander, form, verifying, success
  const [preLanderData, setPreLanderData] = useState(null);
  const [formData, setFormData] = useState(null);

  const handlePreLanderComplete = (answers) => {
    setPreLanderData(answers);
    setStep('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormSubmit = (data) => {
    setFormData(data);
    setStep('verifying');
  };

  const handleVerificationComplete = async () => {
    // Submit only the full form data to backend (not prelander data)
    try {
      const formattedPhone = formData.phone.replace(/\D/g, '');
      const fullPhone = formattedPhone.startsWith('1') ? `+${formattedPhone}` : `+1${formattedPhone}`;
      
      const formattedData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: fullPhone,
        dateOfBirth: formData.dateOfBirth,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        ssnLast4: formData.ssnLast4,
        loanAmount: formData.loanAmount,
        loanPurpose: 'payday_loan',
        employmentStatus: formData.employmentStatus,
        monthlyIncome: formData.monthlyIncome,
        payFrequency: formData.payFrequency,
        bankAccountType: formData.bankAccountType,
        creditScore: formData.creditScore,
        loanType: 'Payday Loan',
        submissionSource: 'us_payday_professional_form',
        userAgent: navigator.userAgent,
        submissionTime: new Date().toISOString()
      };

      const result = await leadService.addLead(formattedData);
      
      if (result.success) {
        setStep('success');
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Unable to submit application. Please try again.', {
        duration: 4000,
        position: 'top-center'
      });
      setStep('form'); // Go back to form
    }
  };

  if (step === 'prelander') {
    return <PreLander onComplete={handlePreLanderComplete} />;
  }

  if (step === 'form') {
    return <LoanForm preLanderData={preLanderData} onVerifying={handleFormSubmit} />;
  }

  if (step === 'verifying') {
    return <VerificationScreen onComplete={handleVerificationComplete} />;
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-8 sm:p-10 max-w-lg w-full text-center">
          
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-100 border border-emerald-200 rounded-full mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>

          <h2 className="text-3xl font-semibold text-slate-900 mb-3">
            Application Submitted!
          </h2>
          
          <p className="text-base text-slate-600 mb-6 leading-relaxed">
            Thank you, <strong className="text-slate-900">{formData.firstName}</strong>! Your application has been securely submitted and is being reviewed by our lending partners.
          </p>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6 text-left">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              What Happens Next?
            </h3>
            <ul className="space-y-2.5 text-sm text-slate-700">
              <li className="flex gap-3">
                <span className="text-emerald-600 font-semibold flex-shrink-0">1.</span>
                <span><strong>Check your email</strong> ({formData.email}) for lender details</span>
              </li>
              <li className="flex gap-3">
                <span className="text-emerald-600 font-semibold flex-shrink-0">2.</span>
                <span><strong>Expect a call</strong> within 24 hours at {formData.phone}</span>
              </li>
              <li className="flex gap-3">
                <span className="text-emerald-600 font-semibold flex-shrink-0">3.</span>
                <span><strong>Have ready:</strong> Bank account details and photo ID</span>
              </li>
            </ul>
          </div>

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-4 mb-6 flex-wrap text-xs text-slate-500 border-t border-slate-200 pt-5">
            <div className="flex items-center gap-1.5">
              <Lock className="w-4 h-4 text-emerald-600" />
              <span>Secure</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Shield className="w-4 h-4 text-emerald-600" />
              <span>Confidential</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <span>Verified</span>
            </div>
          </div>

          <button 
            onClick={() => window.location.href = '/'} 
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3.5 px-6 rounded-lg transition-all"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default LoanFunnel;

